<script lang="ts">
  import InventoryTab from './InventoryTab.svelte';
  import InsightsTab from './InsightsTab.svelte';
  import MaintenanceTab from './MaintenanceTab.svelte';
  import CategoriesTab from './CategoriesTab.svelte';
  import { Button } from '@aatmanova/ui';
  import type { AatmanovaClient } from '@aatmanova/sdk';

  interface Props {
    client: AatmanovaClient;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
    invalidateProducts: () => void;
    searchQuery: string;
    selectedCategoryFilter: string;
    categories: any[];
    filteredProducts: any[];
    productsQueryIsLoading: boolean;
    openCreateProduct: () => void;
    openEditProduct: (product: any) => void;
    handleDeleteProduct: (id: string) => void;
    handleCreateCategory?: (data: { name: string; slug: string; description?: string }) => Promise<any>;
    handleUpdateCategory?: (id: string, data: { name?: string; slug?: string; description?: string }) => Promise<any>;
    handleDeleteCategory?: (id: string) => Promise<any>;
    handleCreateSubcategory?: (data: { categoryId: string; name: string; slug: string; description?: string }) => Promise<any>;
    handleUpdateSubcategory?: (id: string, data: { name?: string; slug?: string; description?: string }) => Promise<any>;
    handleDeleteSubcategory?: (id: string) => Promise<any>;
  }

  let {
    client,
    addToast,
    invalidateProducts,
    searchQuery = $bindable(),
    selectedCategoryFilter = $bindable(),
    categories,
    filteredProducts,
    productsQueryIsLoading,
    openCreateProduct,
    openEditProduct,
    handleDeleteProduct,
    handleCreateCategory,
    handleUpdateCategory,
    handleDeleteCategory,
    handleCreateSubcategory,
    handleUpdateSubcategory,
    handleDeleteSubcategory
  }: Props = $props();

  // ─── Subtab Routing ───────────────────────────────────────────────────────
  type TabType = 'matrix' | 'categories' | 'insights' | 'protocols';
  let activeSubTab = $state<TabType>('matrix');
  let isImportOpen = $state(false);

  async function downloadExport(format: 'csv' | 'excel' | 'zip') {
    try {
      let path = '/api/v1/products/export';
      if (format === 'zip') path = '/api/v1/products/export?format=zip';
      if (format === 'excel') path = '/api/v1/products/export?format=xlsx';
      const res = await fetch(`${client.baseUrl}${path}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Export request failed`);
      const blob = await res.blob();
      const filename = format === 'zip' ? 'products_export.zip' : format === 'excel' ? 'products_export.xlsx' : 'products_export.csv';
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      addToast(`Downloaded catalog in ${format.toUpperCase()} format.`, 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed to export products catalog', 'error');
    }
  }

  const TABS: { id: TabType; label: string }[] = [
    { id: 'matrix',     label: 'Inventory'    },
    { id: 'categories', label: 'Categories'   },
    { id: 'insights',   label: 'Insights'     },
    { id: 'protocols',  label: 'Maintenance'  }
  ];

  const VALID_TABS = TABS.map(t => t.id);

  // Initialise from URL on mount
  $effect(() => {
    if (typeof window === 'undefined') return;
    const subtab = new URLSearchParams(window.location.search).get('subtab');
    if (subtab && VALID_TABS.includes(subtab as TabType)) {
      activeSubTab = subtab as TabType;
    }
  });

  // Sync tab changes → URL
  $effect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get('subtab') || 'matrix';
    if (current !== activeSubTab) {
      if (activeSubTab === 'matrix') url.searchParams.delete('subtab');
      else url.searchParams.set('subtab', activeSubTab);
      window.history.pushState({}, '', url.pathname + url.search);
    }
  });

  // Handle browser back/forward
  $effect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const subtab = new URLSearchParams(window.location.search).get('subtab') || 'matrix';
      if (VALID_TABS.includes(subtab as TabType)) activeSubTab = subtab as TabType;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  });
</script>

<div class="space-y-6">
  <!-- Page Header -->
  <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
    <div class="space-y-1">
      <h2 class="text-3xl font-extrabold tracking-tight text-base-content">Product Catalog</h2>
      <p class="text-sm text-base-content/70">Manage catalog inventory, stock levels, and administrative sync operations.</p>
    </div>

    {#if activeSubTab === 'matrix'}
      <div class="flex flex-wrap items-center gap-2 self-stretch sm:self-start lg:self-auto justify-start sm:justify-end">
        <!-- Export Dropdown -->
        <div class="dropdown dropdown-end">
          <div role="button" tabindex="0" class="btn btn-outline btn-sm font-bold border-base-300 hover:bg-base-200">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
            Export
          </div>
          <ul class="dropdown-content menu p-2 shadow-2xl bg-base-100 border border-base-300 rounded-box w-44 z-[60]">
            <li><button onclick={() => downloadExport('csv')} class="text-xs font-semibold">CSV Standard</button></li>
            <li><button onclick={() => downloadExport('excel')} class="text-xs font-semibold">Excel Sheet (.xlsx)</button></li>
            <li><button onclick={() => downloadExport('zip')} class="text-xs font-semibold">Archive ZIP (with Images)</button></li>
          </ul>
        </div>

        <Button onclick={() => isImportOpen = !isImportOpen} variant="outline" size="sm" class="font-bold border-base-300 hover:bg-base-200">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
          Import
        </Button>

        <Button onclick={openCreateProduct} variant="primary" size="sm" class="font-bold shadow-lg shadow-primary/20">
          + Create Product
        </Button>
      </div>
    {/if}
  </div>

  <!-- Subtab Bar (Horizontally scrollable on mobile) -->
  <div class="flex bg-base-100 border border-base-300 rounded-xl p-1 gap-0.5 w-full max-w-xl overflow-x-auto scrollbar-none">
    {#each TABS as tab}
      <button
        type="button"
        onclick={() => activeSubTab = tab.id}
        class="flex-1 shrink-0 py-1.5 px-3 text-xs font-bold whitespace-nowrap rounded-lg transition-all duration-200 {
          activeSubTab === tab.id
            ? 'bg-primary text-primary-content shadow-sm'
            : 'text-base-content/60 hover:text-base-content hover:bg-base-200'
        }"
      >
        {tab.label}
      </button>
    {/each}
  </div>

  <!-- ─── Tab: Inventory Matrix ─── -->
  {#if activeSubTab === 'matrix'}
    <InventoryTab
      {client}
      {addToast}
      {invalidateProducts}
      bind:searchQuery
      bind:selectedCategoryFilter
      {categories}
      {filteredProducts}
      {productsQueryIsLoading}
      {openCreateProduct}
      {openEditProduct}
      {handleDeleteProduct}
      bind:isImportOpen={isImportOpen}
    />
  {/if}

  <!-- ─── Tab: Categories ─── -->
  {#if activeSubTab === 'categories'}
    <CategoriesTab
      {categories}
      {addToast}
      onCreateCategory={handleCreateCategory ?? (() => Promise.resolve())}
      onUpdateCategory={handleUpdateCategory ?? (() => Promise.resolve())}
      onDeleteCategory={handleDeleteCategory ?? (() => Promise.resolve())}
      onCreateSubcategory={handleCreateSubcategory ?? (() => Promise.resolve())}
      onUpdateSubcategory={handleUpdateSubcategory ?? (() => Promise.resolve())}
      onDeleteSubcategory={handleDeleteSubcategory ?? (() => Promise.resolve())}
    />
  {/if}

  <!-- ─── Tab: Insights ─── -->
  {#if activeSubTab === 'insights'}
    <InsightsTab {filteredProducts} />
  {/if}

  <!-- ─── Tab: Maintenance ─── -->
  {#if activeSubTab === 'protocols'}
    <MaintenanceTab {client} {addToast} {invalidateProducts} />
  {/if}
</div>

<style>
  .scrollbar-none {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .scrollbar-none::-webkit-scrollbar {
    display: none;
  }
</style>
